import { Button } from "@gigpro/ui/Button";
import { Delete, Plus } from "@gigpro/ui/Icons";
import { Label } from "@gigpro/ui/Label";
import { cn } from "@gigpro/ui/tailwind/cn";
import { TimeField } from "@gigpro/ui/TimeField";
import { ToggleGroup, ToggleGroupItem } from "@gigpro/ui/ToggleGroup";
import React from "react";
import type { FieldErrors } from "react-hook-form";

/** Not a datetime: wall-clock times on weekday slots, so nothing converts between timezones. */
export interface AvailabilityWindow {
  /** 0 = Monday … 6 = Sunday. */
  days: Array<number>;
  /** 24h `"HH:mm"`. */
  start: string;
  /** 24h `"HH:mm"`. */
  end: string;
}

/** A factory, not a constant: two unedited rows would otherwise share one object and its `days` array. */
const createWindow = (): AvailabilityWindow => ({ days: [0, 1, 2, 3, 4], start: "09:00", end: "17:00" });

export const WEEKDAYS = [
  { index: 0, initial: "M", name: "Monday" },
  { index: 1, initial: "T", name: "Tuesday" },
  { index: 2, initial: "W", name: "Wednesday" },
  { index: 3, initial: "T", name: "Thursday" },
  { index: 4, initial: "F", name: "Friday" },
  { index: 5, initial: "S", name: "Saturday" },
  { index: 6, initial: "S", name: "Sunday" },
] as const;

/** A fixed date, not today: a DST transition day would shift the rendered time. */
const toDate = (time: string): Date => new Date(`2000-01-01T${time}`);

const toWireTime = (date: Date): string => date.toTimeString().slice(0, 5);

interface TimeInputProps {
  value: string;
  onValueChange: (value: string) => void;
  label: string;
  invalid: boolean;
  disabled?: boolean;
}

const TimeInput = ({ value, onValueChange, label, invalid, disabled }: Readonly<TimeInputProps>) => (
  <TimeField
    value={toDate(value)}
    // `null` mid-clear: the field shows that partial state itself, so `start`/`end` never go empty.
    onValueChange={(date) => date && onValueChange(toWireTime(date))}
    // Wall-clock, so a browser-timezone caption would be wrong.
    hideTimezone
    invalid={invalid}
    disabled={disabled}
    aria-label={label}
    className="w-32"
  />
);

export interface AvailabilityWindowsFieldProps {
  /** An empty array is a valid state, not an error. */
  value: Array<AvailabilityWindow>;
  onChange: (windows: Array<AvailabilityWindow>) => void;
  label?: React.ReactNode;
  description?: React.ReactNode;
  error?: NonNullable<FieldErrors<{ windows: Array<AvailabilityWindow> }>["windows"]>;
  disabled?: boolean;
  /** Replaces the default empty-state copy; error copy still wins. */
  emptyState?: React.ReactNode;
  className?: string;
}

const DEFAULT_EMPTY_STATE = (
  <>
    <p className="text-normal font-semibold text-gray">No windows yet</p>
    <p className="text-small text-gray">Windows tell us when you're available to interview.</p>
  </>
);

export function AvailabilityWindowsField({
  value,
  onChange,
  label,
  description,
  error,
  disabled,
  emptyState = DEFAULT_EMPTY_STATE,
  className,
}: Readonly<AvailabilityWindowsFieldProps>) {
  const updateWindow = (index: number, patch: Partial<AvailabilityWindow>) =>
    onChange(value.map((existing, i) => (i === index ? { ...existing, ...patch } : existing)));

  // An overlap belongs to a pair, so zod lands it on the array's `message`, not on an indexed row.
  const fieldError = error && "message" in error ? error.message : undefined;

  return (
    <div className={cn("@container/availability flex flex-col gap-3", className)}>
      {!!label && <Label className="text-normal font-semibold">{label}</Label>}
      {!!description && <p className="text-small text-gray">{description}</p>}
      {value.length === 0 ? (
        <div className="flex min-h-26 flex-col justify-center gap-1 rounded-sm border border-dashed border-light-gray p-5 text-center">
          {fieldError ? <p className="text-normal font-semibold text-error">{fieldError}</p> : emptyState}
        </div>
      ) : (
        // Two windows abreast once the field itself is wide — the Settings card, not the dialog.
        <div className="grid gap-3 @2xl/availability:grid-cols-2">
          {value.map((interviewWindow, index) => {
            const rowError = error?.[index];
            const timeError = rowError?.start?.message ?? rowError?.end?.message;
            const message = rowError?.days?.message ?? timeError;
            const rowLabel = `window ${index + 1}`;
            return (
              // Safe: a row holds no state, so a re-index after Remove cannot mis-attach any.
              // eslint-disable-next-line react/no-array-index-key
              <div
                // oxlint-disable-next-line react/no-array-index-key -- static list, index is the stable key
                key={index}
                className="flex min-h-26 flex-col justify-center gap-4 rounded-sm border border-light-gray p-3"
              >
                {/* Two rows: on one, a wrap strands "9:00 am … to" above "5:00 pm". */}
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <ToggleGroup
                    type="multiple"
                    value={interviewWindow.days.map(String)}
                    // Sorted, not press order, or a re-pressed day reads as an edit to the dirty check.
                    onValueChange={(days) => updateWindow(index, { days: days.map(Number).toSorted((a, b) => a - b) })}
                    disabled={disabled}
                    aria-label={`Days for ${rowLabel}`}
                    className="gap-1 bg-transparent p-0"
                  >
                    {WEEKDAYS.map((day) => (
                      <ToggleGroupItem
                        key={day.name}
                        value={String(day.index)}
                        aria-label={`${day.name} for ${rowLabel}`}
                        // `p-0` so padding cannot fight `size-8` and inflate the circle.
                        className="size-8 shrink-0 items-center justify-center rounded-full bg-light-gray p-0 text-brand"
                      >
                        {day.initial}
                      </ToggleGroupItem>
                    ))}
                  </ToggleGroup>
                  <Button
                    variant="text"
                    icon
                    onClick={() => onChange(value.toSpliced(index, 1))}
                    disabled={disabled}
                    aria-label={`Remove ${rowLabel}`}
                  >
                    <Delete />
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <TimeInput
                    value={interviewWindow.start}
                    onValueChange={(start) => updateWindow(index, { start })}
                    label={`Start time for ${rowLabel}`}
                    invalid={!!timeError}
                    disabled={disabled}
                  />
                  <span className="text-normal text-gray">to</span>
                  <TimeInput
                    value={interviewWindow.end}
                    onValueChange={(end) => updateWindow(index, { end })}
                    label={`End time for ${rowLabel}`}
                    invalid={!!timeError}
                    disabled={disabled}
                  />
                </div>
                {!!message && <p className="text-small font-semibold text-error">{message}</p>}
              </div>
            );
          })}
        </div>
      )}
      {/* Rows only: the empty branch already shows it in place of its neutral copy. */}
      {!!fieldError && value.length > 0 && <p className="text-small font-semibold text-error">{fieldError}</p>}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Button size="small" onClick={() => onChange([...value, createWindow()])} disabled={disabled}>
          <Plus /> Add window
        </Button>
      </div>
    </div>
  );
}
